/**
 * syncHub.ts — what the sync hub shows about outgoing changes.
 *
 * Turns raw outbox items into one row per thing the user recognizes (a page's
 * text, its saved versions, a folder's details, a setting), and turns a
 * refused item into a plain explanation with the fixes that fit its cause.
 * Pure functions; the modal supplies names, locations and the server check.
 */

import type { StoredMutation } from './webStorage'
import type { DocumentServerStatus } from './syncEngine'
import type { SyncLogEntry } from './syncLog'

export type OutgoingKind = 'text' | 'versions' | 'deletion' | 'row' | 'setting' | 'edits'
export type HubAction = 'retry' | 'reupload' | 'resend' | 'discard' | 'save-copy' | 'open'

/** Where the target lives on this device. */
export interface LocalTarget {
  name: string
  /** Containing folders, outermost first. */
  path: string[]
  inTrash: boolean
  subtype: 'page' | 'folder' | 'desk'
}

export interface OutgoingIssue {
  title: string
  detail: string
  actions: HubAction[]
}

export interface OutgoingRow {
  key: string
  kind: OutgoingKind
  targetId: string
  /** The local page or folder, if this device still has it. */
  local: LocalTarget | null
  itemIds: string[]
  /** Changes in the row (saved versions, or journaled edits for 'edits'). */
  count: number
  /** 'orphaned': unsent edits of a page this device no longer has. */
  state: 'waiting' | 'retrying' | 'failed' | 'unsent-edits' | 'orphaned'
  since: number
  attempts: number
  nextAttemptAt?: number
  lastError?: string
  httpStatus?: number
  issue?: OutgoingIssue
}

export const KIND_LABELS: Record<OutgoingKind, string> = {
  text: 'Page text',
  versions: 'Saved versions',
  deletion: 'Permanent deletion',
  row: 'Name, place or style',
  setting: 'Setting',
  edits: 'Unsent edits',
}

function payloadOf(item: StoredMutation): any {
  try {
    return typeof item.payload === 'string' ? JSON.parse(item.payload) : item.payload
  } catch {
    return null
  }
}

function classify(item: StoredMutation): { kind: OutgoingKind; targetId: string } {
  if (item.type === 'project') return { kind: 'row', targetId: item.targetId }
  if (item.type === 'settings') return { kind: 'setting', targetId: item.targetId }
  if (item.type === 'snapshot') return { kind: 'versions', targetId: payloadOf(item)?.documentId || item.targetId }
  return { kind: item.action === 'delete' ? 'deletion' : 'text', targetId: item.targetId }
}

/**
 * Groups items into rows. A row is 'failed' if any of its items was set
 * aside; refused and waiting items for the same target are kept apart so a
 * retry or discard only touches what the user is looking at.
 */
export function buildOutgoingRows(
  items: StoredMutation[],
  dormantPages: Array<{ docId: string; edits: number; since: number; status?: 'pending' | 'failed'; attempts?: number; nextAttemptAt?: number; lastError?: string; httpStatus?: number }>,
  locate: (id: string) => LocalTarget | null,
  serverStatus: Map<string, DocumentServerStatus>,
): OutgoingRow[] {
  const rows = new Map<string, OutgoingRow>()

  for (const item of items) {
    const { kind, targetId } = classify(item)
    const failed = item.status === 'failed'
    const key = `${kind}:${targetId}:${failed ? 'failed' : 'waiting'}`
    let row = rows.get(key)
    if (!row) {
      row = {
        key,
        kind,
        targetId,
        local: kind === 'setting' ? null : locate(targetId),
        itemIds: [],
        count: 0,
        state: failed ? 'failed' : 'waiting',
        since: item.firstQueuedAt ?? item.timestamp,
        attempts: 0,
      }
      rows.set(key, row)
    }
    row.itemIds.push(item.id)
    row.count++
    row.since = Math.min(row.since, item.firstQueuedAt ?? item.timestamp)
    if ((item.attempts ?? 0) >= row.attempts) {
      row.attempts = item.attempts ?? 0
      row.lastError = item.lastError
      row.httpStatus = item.httpStatus
    }
    if (!failed && item.attempts) {
      row.state = 'retrying'
      if (item.nextAttemptAt && (!row.nextAttemptAt || item.nextAttemptAt < row.nextAttemptAt)) {
        row.nextAttemptAt = item.nextAttemptAt
      }
    }
  }

  for (const page of dormantPages) {
    const local = locate(page.docId)
    rows.set(`edits:${page.docId}`, {
      key: `edits:${page.docId}`,
      kind: 'edits',
      targetId: page.docId,
      local,
      itemIds: [],
      count: page.edits,
      state: !local ? 'orphaned' : page.status === 'failed' ? 'failed' : page.attempts ? 'retrying' : 'unsent-edits',
      since: page.since,
      attempts: page.attempts || 0,
      nextAttemptAt: page.nextAttemptAt,
      lastError: page.lastError,
      httpStatus: page.httpStatus,
      // A missing local tree row needs a recovery copy or an explicit discard.
      issue: local ? undefined : {
        title: 'Page no longer on this device',
        detail: 'These edits belong to a page that was deleted here, so they can’t be sent. Save a copy if you want the text, then discard them.',
        actions: ['save-copy', 'discard'],
      },
    })
  }

  for (const row of rows.values()) {
    if (row.state === 'failed') row.issue = row.kind === 'edits' ? { title: 'Page edits need attention', detail: row.lastError || 'Background delivery was refused; local edits remain on this device.', actions: ['retry', 'save-copy'] } : explainFailure(row, serverStatus.get(row.targetId))
  }

  return [...rows.values()].sort((a, b) => a.since - b.since)
}

/** Page ids worth asking the server about: refused page-related rows. */
export function idsToCheck(rows: OutgoingRow[]): string[] {
  return [...new Set(rows
    .filter((row) => row.state === 'failed' && (row.kind === 'text' || row.kind === 'versions' || row.kind === 'deletion'))
    .map((row) => row.targetId))]
}

/** Why a set-aside row was refused, in plain words, and what can be done. */
export function explainFailure(row: OutgoingRow, server?: DocumentServerStatus): OutgoingIssue {
  const status = row.httpStatus
  const pageGone = row.kind !== 'setting' && row.kind !== 'deletion' && !row.local

  if (pageGone) {
    return {
      title: 'No longer on this device',
      detail: row.kind === 'text'
        ? 'This page was removed here before its text reached the cloud, so the upload has nowhere to go. Save a copy if you still want this text.'
        : 'This was removed here before it reached the cloud, so the upload has nowhere to go.',
      actions: row.kind === 'text' ? ['save-copy', 'discard'] : ['discard'],
    }
  }

  if (row.kind === 'deletion') {
    if (status === 409) {
      return {
        title: 'Not in the cloud’s Trash',
        detail: 'The cloud still has this page outside the Trash, so it won’t delete it permanently. Try again once this device’s other changes have synced, or discard to leave the page in the cloud.',
        actions: ['retry', 'discard'],
      }
    }
    return generic(row)
  }

  if ((row.kind === 'text' || row.kind === 'versions') && status === 404) {
    if (server?.state === 'purged') {
      return {
        title: 'Permanently deleted in the cloud',
        detail: 'This page was permanently deleted from the cloud, probably on another device, but it still exists here. Upload it again to put it back, or discard these changes.',
        actions: ['reupload', 'discard'],
      }
    }
    if (server?.state === 'missing') {
      return {
        title: 'The cloud doesn’t have this page',
        detail: 'Its name and place never reached the cloud, so the cloud won’t take its text. Upload it again to fix this.',
        actions: ['reupload', 'discard'],
      }
    }
    if (server?.state === 'active' || server?.state === 'trashed') {
      return {
        title: 'The cloud has this page now',
        detail: 'It wasn’t there yet when this was sent. Trying again should work.',
        actions: ['retry', 'discard'],
      }
    }
    return {
      title: 'The cloud couldn’t find this page',
      detail: 'Uploading the page again usually fixes this.',
      actions: ['reupload', 'retry', 'discard'],
    }
  }

  if (row.kind === 'row' && status !== undefined && status >= 400 && status < 500 && status !== 429 && status !== 403) {
    return {
      title: 'The cloud refused these details',
      detail: `The server couldn’t accept this ${row.local?.subtype ?? 'item'}’s name, place or style (${row.lastError ?? `HTTP ${status}`}). Sending its current state again usually fixes this.`,
      actions: ['resend', 'discard'],
    }
  }

  if (status === 413) {
    return {
      title: 'Too large to upload',
      detail: 'This is larger than the cloud accepts. It is kept on this device.',
      actions: ['discard'],
    }
  }
  if (status === 403) {
    return {
      title: 'Not allowed',
      detail: 'Your account isn’t allowed to change this in the cloud.',
      actions: ['discard'],
    }
  }
  if (status !== undefined && (status >= 500 || status === 429 || status === 408)) {
    return {
      title: 'The server kept failing',
      detail: `The cloud failed ${row.attempts} times in a row (${row.lastError ?? `HTTP ${status}`}). This is usually temporary.`,
      actions: ['retry', 'discard'],
    }
  }
  return generic(row)
}

function generic(row: OutgoingRow): OutgoingIssue {
  return {
    title: 'The cloud refused this',
    detail: row.lastError ?? 'The server didn’t say why.',
    actions: ['retry', 'discard'],
  }
}

/** What discarding a row loses, for the confirmation. */
export function discardConsequence(row: OutgoingRow): string {
  switch (row.kind) {
    case 'text':
      return row.local
        ? 'Your text stays on this device. Only this upload is canceled; other devices keep the cloud’s older copy until the page is edited again.'
        : 'This page is no longer on this device, so this upload is the only copy of its text. Discarding it loses the text for good.'
    case 'versions':
      return 'These saved versions stay on this device but won’t appear on your other devices.'
    case 'deletion':
      return 'The page stays in the cloud, so your other devices will keep it.'
    case 'row':
      return 'Your other devices won’t see these changes to its name, place or style until it changes again here.'
    case 'setting':
      return 'Your other devices keep their current value for this setting.'
    case 'edits':
      return 'The page these edits belong to was deleted on this device, so they can’t be sent. Discarding removes them for good.'
    default:
      return 'This change won’t reach the cloud.'
  }
}

/** The plain text of a queued page upload, for saving a copy. */
export function queuedPageText(item: StoredMutation): { text: string; markdown: boolean } | null {
  const payload = payloadOf(item)
  const content = payload?.content
  if (typeof content !== 'string') return null
  if (payload.format === 'markdown') return { text: content, markdown: true }
  try {
    return { text: tiptapToPlainText(JSON.parse(content)), markdown: false }
  } catch {
    return { text: content, markdown: false }
  }
}

const BLOCK_TYPES = new Set(['paragraph', 'heading', 'blockquote', 'listItem', 'taskItem', 'codeBlock', 'horizontalRule'])

export function tiptapToPlainText(node: any): string {
  const blocks: string[] = []
  let line = ''
  const walk = (current: any) => {
    if (!current || typeof current !== 'object') return
    if (current.type === 'text' && typeof current.text === 'string') line += current.text
    else if (current.type === 'hardBreak') line += '\n'
    const isBlock = BLOCK_TYPES.has(current.type)
    if (isBlock && line) { blocks.push(line); line = '' }
    for (const child of current.content ?? []) walk(child)
    if (isBlock && line) { blocks.push(line); line = '' }
  }
  walk(node)
  if (line) blocks.push(line)
  return blocks.join('\n\n')
}

/**
 * One row as text, for "Copy details". Ids and errors only, never titles, so
 * it can be pasted into a support request without revealing any writing.
 */
export function describeRow(row: OutgoingRow, now = Date.now()): string {
  const lines = [
    KIND_LABELS[row.kind],
    `Id: ${row.targetId}${row.kind !== 'setting' && !row.local ? ' (not on this device)' : ''}`,
    `State: ${row.state}${row.attempts ? ` after ${row.attempts} attempt${row.attempts === 1 ? '' : 's'}` : ''}`,
    `Waiting since: ${new Date(row.since).toISOString()} (${Math.round((now - row.since) / 60_000)} min)`,
  ]
  if (row.count > 1) lines.push(`Changes: ${row.count}`)
  if (row.lastError) lines.push(`Last error: ${row.lastError}`)
  if (row.issue) lines.push(`Diagnosis: ${row.issue.title}`)
  return lines.join('\n')
}

// ── Diagnostic report ────────────────────────────────────────────────────────

export interface DiagnosticInput {
  generatedAt: number
  appVersion: string
  platform: string
  online: boolean
  syncStatus: string
  summary: { health: string; label: string }
  waiting: number
  failed: number
  pages: number
  serverUnreachable: boolean
  lastSentAt: number | null
  lastCheckedAt: number | null
  ratePerMinute: number
  nextRetryAt: number | null
  rows: OutgoingRow[]
  connections: Record<string, string>
  log: Array<Pick<SyncLogEntry, 'at' | 'direction' | 'level' | 'kind' | 'message' | 'targetIds' | 'items' | 'repeats' | 'httpStatus'>>
}

const iso = (at: number | null) => (at ? new Date(at).toISOString() : 'never')
const ARROWS: Record<SyncLogEntry['direction'], string> = { out: '↑', in: '↓', system: '•' }

export function formatLogLine(line: DiagnosticInput['log'][number]): string {
  const ids = line.targetIds?.length
    ? ` [${line.targetIds.slice(0, 5).join(', ')}${line.targetIds.length > 5 ? ` +${line.targetIds.length - 5}` : ''}]`
    : ''
  const extra = [
    line.items !== undefined ? `${line.items} ${line.items === 1 ? 'item' : 'items'}` : '',
    line.repeats > 1 ? `×${line.repeats}` : '',
  ].filter(Boolean).join(', ')
  return `${iso(line.at)} ${ARROWS[line.direction]} ${line.level.toUpperCase()} ${line.kind}: ${line.message}${ids}${extra ? ` (${extra})` : ''}`
}

/**
 * A plain-text report for a support request. Ids, counts, states and errors
 * only: never a title, a setting's value, or any writing.
 */
export function buildDiagnosticReport(input: DiagnosticInput, logLimit = 200): string {
  const sections: string[] = []
  sections.push([
    'Superelectric Ink sync report',
    `Generated: ${iso(input.generatedAt)}`,
    `App: ${input.appVersion}`,
    `Platform: ${input.platform}`,
    `Online: ${input.online ? 'yes' : 'no'}`,
  ].join('\n'))

  sections.push([
    '== Status ==',
    `Sync: ${input.syncStatus} · ${input.summary.health} · "${input.summary.label}"`,
    `Waiting: ${input.waiting} · Need attention: ${input.failed} · Closed pages with unsent edits: ${input.pages}`,
    `Server unreachable: ${input.serverUnreachable ? 'yes' : 'no'} · Next retry: ${iso(input.nextRetryAt)}`,
    `Last sent: ${iso(input.lastSentAt)} · Last checked for changes: ${iso(input.lastCheckedAt)}`,
    `Speed: ${input.ratePerMinute.toFixed(1)} items/min`,
  ].join('\n'))

  const connections = Object.entries(input.connections)
  sections.push([
    `== Live page connections (${connections.length}) ==`,
    ...(connections.length ? connections.map(([id, status]) => `${id}: ${status}`) : ['none']),
  ].join('\n'))

  const rows = input.rows.slice(0, 100)
  sections.push([
    `== Outgoing (${input.rows.length}) ==`,
    ...(rows.length ? rows.map((row) => describeRow(row, input.generatedAt)) : ['nothing waiting']),
    ...(input.rows.length > rows.length ? [`… and ${input.rows.length - rows.length} more`] : []),
  ].join('\n\n'))

  const lines = input.log.slice(0, logLimit)
  sections.push([
    `== Sync log (newest first, ${lines.length} of ${input.log.length}) ==`,
    ...(lines.length ? lines.map(formatLogLine) : ['empty']),
  ].join('\n'))

  return sections.join('\n\n')
}
